import { Package, Flag, GitBranch } from "lucide-react";
import { cn } from "@/lib/utils";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";

/**
 * LR Direct — full-width horizontal ROUTE TRACKER (display only).
 *
 * Reads the load's real stops (direct_load_stops, already fetched by the
 * caller) and renders them left-to-right, Domino's-tracker style:
 * pickups first (teal, package icon), final delivery distinct (brand yellow,
 * flag icon). Multi-stop loads get a "Multi-pick load" badge + summary line.
 * Nothing here touches bidding, deadlines or the quote flow.
 */

export interface RouteTrackerStop {
  id?: string;
  stop_number?: number | null;
  stop_type?: string | null;
  city?: string | null;
  state?: string | null;
  scheduled_date?: string | null;
}

interface Props {
  stops: RouteTrackerStop[];
  /** Fallback origin/destination when the load has no stop rows (legacy). */
  fallback?: {
    originCity?: string | null;
    originState?: string | null;
    destCity?: string | null;
    destState?: string | null;
    pickupDate?: string | null;
    deliveryDate?: string | null;
  };
  miles?: number | null;
  className?: string;
}

function shortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? parseYYYYMMDDToLocalDate(value)
    : new Date(value);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function RouteTrackerBar({ stops, fallback, miles, className }: Props) {
  const sorted = [...(stops ?? [])].sort(
    (a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0),
  );

  const nodes: { key: string; label: string; isPickup: boolean; city: string; date: string | null }[] =
    sorted.length >= 2
      ? (() => {
          const picks = sorted.filter((s) => s.stop_type === "pickup");
          const drops = sorted.filter((s) => s.stop_type !== "pickup");
          let pickIdx = 0;
          let dropIdx = 0;
          return sorted.map((s, i) => {
            const isPickup = s.stop_type === "pickup";
            const label = isPickup
              ? picks.length > 1
                ? `Pick ${++pickIdx}`
                : "Pickup"
              : drops.length > 1
                ? `Drop ${++dropIdx}`
                : "Drop";
            return {
              key: s.id ?? `stop-${i}`,
              label,
              isPickup,
              city: [s.city, s.state].filter(Boolean).join(", ") || "—",
              date: shortDate(s.scheduled_date),
            };
          });
        })()
      : [
          {
            key: "fb-origin",
            label: "Pickup",
            isPickup: true,
            city: [fallback?.originCity, fallback?.originState].filter(Boolean).join(", ") || "—",
            date: shortDate(fallback?.pickupDate),
          },
          {
            key: "fb-dest",
            label: "Drop",
            isPickup: false,
            city: [fallback?.destCity, fallback?.destState].filter(Boolean).join(", ") || "—",
            date: shortDate(fallback?.deliveryDate),
          },
        ];

  const pickCount = nodes.filter((n) => n.isPickup).length;
  const dropCount = nodes.length - pickCount;
  const isMulti = nodes.length > 2;
  const milesStr = miles && miles > 0 ? `~${miles.toLocaleString()} mi` : null;

  return (
    <div className={cn("w-full rounded-xl border border-gray-200 bg-white px-3 py-2", className)}>
      {/* Header */}
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        {isMulti ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-brand-teal">
            <GitBranch className="h-3 w-3" />
            {pickCount > 1 ? "Multi-pick load" : "Multi-stop load"}
          </span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Route
          </span>
        )}
        <span className="text-[11px] text-muted-foreground">
          {pickCount} pickup{pickCount === 1 ? "" : "s"} · {dropCount} drop{dropCount === 1 ? "" : "s"}
          {milesStr ? ` · ${milesStr}` : ""}
        </span>
      </div>

      {/* Horizontal tracker (sm and up) */}
      <div className="hidden sm:block">
        <div className="relative flex w-full items-center">
          <div
            className="absolute top-1/2 h-0.5 -translate-y-1/2 bg-border"
            style={{ left: `calc(100% / ${nodes.length * 2})`, right: `calc(100% / ${nodes.length * 2})` }}
          />
          {nodes.map((n) => (
            <div key={`dot-${n.key}`} className="relative flex flex-1 items-center justify-center">
              <span
                className={cn(
                  "relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-white shadow-sm",
                  n.isPickup ? "bg-brand-teal" : "bg-[#F7CF0D] text-[#0B1625]",
                )}
              >
                {n.isPickup ? <Package className="h-3 w-3" /> : <Flag className="h-3 w-3" />}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-1 flex w-full items-start">
          {nodes.map((n) => (
            <div key={`lbl-${n.key}`} className="min-w-0 flex-1 px-1 text-center">
              <div
                className={cn(
                  "text-[9px] font-bold uppercase tracking-[0.12em]",
                  n.isPickup ? "text-brand-teal" : "text-[#8a7203]",
                )}
              >
                {n.label}
              </div>
              <div className="truncate text-[11px] font-semibold text-foreground">{n.city}</div>
              <div className="text-[10px] tabular-nums text-muted-foreground">{n.date ?? "\u00A0"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Compact vertical list (mobile) */}
      <ol className="ml-3 space-y-2 border-l-2 border-border sm:hidden">
        {nodes.map((n) => (
          <li key={`m-${n.key}`} className="relative pl-4">
            <span
              className={cn(
                "absolute -left-[13px] top-0.5 flex h-6 w-6 items-center justify-center rounded-full text-white",
                n.isPickup ? "bg-brand-teal" : "bg-[#F7CF0D] text-[#0B1625]",
              )}
            >
              {n.isPickup ? <Package className="h-3 w-3" /> : <Flag className="h-3 w-3" />}
            </span>
            <div
              className={cn(
                "text-[9px] font-bold uppercase tracking-[0.12em]",
                n.isPickup ? "text-brand-teal" : "text-[#8a7203]",
              )}
            >
              {n.label}
            </div>
            <div className="text-sm font-semibold text-foreground">{n.city}</div>
            {n.date && <div className="text-[11px] text-muted-foreground">{n.date}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}
